import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { HttpEventType } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs';

import type { ITechnicalLogHandler } from '../telemetry';
import { RESOURCE_TIMING_PROVIDER, TECHNICAL_LOG_HANDLER } from '../telemetry';
import { describePayload } from '../telemetry/models/technical-log-payload.model';
import type { IResourceTimingProvider } from '../telemetry/services/providers/i-resource-timing.provider';

/**
 * I parametri il cui valore non entra nel diario. Il diario viaggia dentro una segnalazione, e questi
 * nomi portano cose che non devono uscire dal browser di chi la manda.
 */
const REDACTED_QUERY_PARAMETERS: readonly string[] = [
  'token',
  'code',
  'key',
  'secret',
  'signature',
  'sig',
  'sas',
  'email',
  'password',
];

const REDACTED_VALUE = '***';

/** Il dettaglio di una riga del diario, mentre lo si mette insieme. */
type Detail = Record<string, string | number | boolean>;

/**
 * I momenti di una chiamata, presi mentre passano. Sono quelli che il pannello di rete di un browser
 * mostra come barre: quanto e' durato mandare la richiesta, quanto si e' aspettato il server, quanto e'
 * durato scaricare la risposta.
 */
interface CallTimings {
  readonly startedAt: number;
  /** Quando la richiesta e' partita verso la rete. */
  sentAt?: number;
  /** Quando l'ultimo byte della richiesta e' uscito: c'e' solo per una richiesta con un corpo. */
  uploadedAt?: number;
  /** Quando e' arrivato il primo byte della risposta: la fine dell'attesa e l'inizio dello scarico. */
  firstByteAt?: number;
}

/**
 * Scrive nel diario tecnico ogni chiamata al BFF **andata storta**: metodo, indirizzo, esito, lo spettro
 * dei tempi e la **forma** dei corpi — quali campi c'erano e cosa ha risposto il server. Una chiamata
 * riuscita non si scrive: il diario serve a spiegare un guasto, e descriverne il corpo sarebbe lavoro a
 * ogni risposta per una riga che l'archivio scarterebbe comunque.
 *
 * Il contenuto dei corpi non esce: `describePayload` lascia passare solo cio' che non puo' essere di
 * qualcuno (numeri, identificativi, codici) e mette `***` con la sola lunghezza al posto di tutto il
 * resto (CLAUDE.md §6). Serve la forma, non le parole: una chiamata andata storta si spiega sapendo che
 * il campo c'era ed era vuoto, non sapendo cosa c'era scritto dentro.
 *
 * Registra dopo l'esito e non prima della richiesta, cosi' ogni riga porta gia' come e' andata: una riga
 * prima e una dopo raddoppierebbero il diario per dire la stessa cosa. L'unica seconda riga e' quella
 * della connessione (`recordConnection`), che esce solo quando ce n'e' stata una da aprire.
 */
export const technicalLogInterceptor: HttpInterceptorFn = (req, next) => {
  const technicalLog = inject(TECHNICAL_LOG_HANDLER);
  const resourceTiming = inject(RESOURCE_TIMING_PROVIDER);
  const timings: CallTimings = { startedAt: Date.now() };
  const message = `${req.method} ${safeUrl(req.url)}`;

  // I tempi intermedi esistono solo se si chiedono: senza `reportProgress` il backend emette la sola
  // risposta finale, e di una chiamata lenta non si saprebbe **dove** e' stata lenta. Gli eventi in piu'
  // non arrivano a chi ha chiamato: `HttpClient` consegna il solo corpo se non gli si chiede altro.
  return next(req.clone({ reportProgress: true })).pipe(
    tap({
      // I tempi si prendono comunque mentre passano: che la chiamata fallira' si sa solo alla fine.
      next: (event) => trackTiming(event, timings),
      error: (error: HttpErrorResponse) => {
        technicalLog.record({
          kind: 'network',
          message,
          detail: {
            // `status` a zero e' la rete che non ha risposto — offline, CORS, richiesta annullata — ed e'
            // una diagnosi diversa da un rifiuto del server: va distinta, non nascosta.
            status: error.status,
            failed: true,
            ...spans(timings),
            // Il corpo di un errore e' il punto: dice quale campo il server ha rifiutato.
            ...payloads(describePayload(req.body), describePayload(error.error)),
          },
        });

        recordConnection(technicalLog, resourceTiming, message, req.url, timings.startedAt);
      },
    }),
  );
};

/**
 * La parte di una chiamata fallita che gli eventi non vedono: trovare l'indirizzo, aprire la connessione,
 * stringere la mano TLS. La misura il browser e la racconta il Resource Timing — su un'altra origine
 * soltanto se la risposta porta `Timing-Allow-Origin`, che il BFF scrive per i portali dichiarati.
 *
 * E' una riga a parte e non si aspetta: la voce arriva un istante dopo l'evento con cui la chiamata si
 * e' chiusa, e far tardare la riga principale per lei significherebbe perderla del tutto se la scheda si
 * chiude nel frattempo. Si scrive solo quando una connessione e' stata davvero aperta: su una riusata i
 * tre tempi sono zero e la riga non direbbe niente che non si sappia gia'.
 */
function recordConnection(
  technicalLog: ITechnicalLogHandler,
  resourceTiming: IResourceTimingProvider,
  message: string,
  url: string,
  startedAt: number,
): void {
  void resourceTiming
    .connectionPhasesFor(url, startedAt)
    .then((phases) => {
      if (!phases || phases.dnsMs + phases.tcpMs + phases.tlsMs === 0) return;

      technicalLog.record({
        kind: 'network',
        message: `${message} connection`,
        detail: {
          // La riga appartiene a una chiamata fallita e lo dice: senza, l'archivio la scarterebbe.
          failed: true,
          dnsMs: phases.dnsMs,
          tcpMs: phases.tcpMs,
          tlsMs: phases.tlsMs,
          ttfbMs: phases.ttfbMs,
          ...(phases.wireBytes === undefined ? {} : { wireBytes: phases.wireBytes }),
        },
      });
    })
    // Il diario non deve poter rompere una chiamata: se la misura non arriva, la riga non c'e' e basta.
    .catch(() => undefined);
}

function trackTiming(event: HttpEvent<unknown>, timings: CallTimings): void {
  switch (event.type) {
    case HttpEventType.Sent:
      timings.sentAt = Date.now();
      break;
    case HttpEventType.UploadProgress:
      // Ogni pezzo che sale sposta la fine dell'invio: l'ultimo che arriva e' la fine vera.
      timings.uploadedAt = Date.now();
      break;
    case HttpEventType.ResponseHeader:
      timings.firstByteAt = Date.now();
      break;
    default:
      break;
  }
}

/**
 * Lo spettro della chiamata, in millisecondi: invio, attesa del server, scarico e totale. Le tre parti
 * escono solo se si sono potute misurare — una risposta senza corpo non fa scattare nessun evento di
 * scarico, e allora di quella chiamata si sa il totale e basta, che e' meglio di un numero inventato.
 */
function spans(timings: CallTimings): Detail {
  const endedAt = Date.now();
  const uploadedAt = timings.uploadedAt ?? timings.sentAt ?? timings.startedAt;

  const detail: Detail = {
    sentMs: uploadedAt - timings.startedAt,
    ms: endedAt - timings.startedAt,
  };

  if (timings.firstByteAt === undefined) return detail;

  return {
    ...detail,
    waitMs: timings.firstByteAt - uploadedAt,
    downloadMs: endedAt - timings.firstByteAt,
  };
}

function payloads(request: string | undefined, response: string | undefined): Detail {
  return {
    ...(request === undefined ? {} : { req: request }),
    ...(response === undefined ? {} : { res: response }),
  };
}

/**
 * L'indirizzo con i soli parametri che si possono scrivere. Si tiene la query — «i parametri e gli esiti»
 * sono meta' di cio' che serve a capire una chiamata — ma i nomi che portano un segreto o un indirizzo di
 * posta escono col valore oscurato invece di far rinunciare all'intera query.
 */
function safeUrl(url: string): string {
  const [path, query] = url.split('?');

  if (!query) return path;

  const redacted = new URLSearchParams(query);

  // I nomi si raccolgono prima e si riscrivono dopo: modificare i parametri mentre li si percorre e'
  // proprio il caso che `URLSearchParams` non garantisce.
  const names: string[] = [];
  redacted.forEach((_value, name) => names.push(name));

  for (const name of names) {
    if (REDACTED_QUERY_PARAMETERS.includes(name.toLowerCase())) redacted.set(name, REDACTED_VALUE);
  }

  return `${path}?${redacted.toString()}`;
}
